import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    InputAdornment,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parseDate, dateToString } from '../../utils/utils.js';

const DatesAndRatesStep = ({ formik }) => {
    const { values, handleChange, setFieldValue, touched, errors, handleBlur } = formik;

    const useCustomValues = values.useCustomValues;
    const fieldDisabled = !useCustomValues;

    // Helper to convert decimal to percentage string for display
    const decimalToPercentageString = (decimalValue, fixedDigits = 2) => {
        if (decimalValue === null || decimalValue === undefined || String(decimalValue).trim() === '') return '';
        const num = parseFloat(decimalValue);
        if (isNaN(num)) return '';
        return (num * 100).toFixed(fixedDigits);
    };

    // Local state for display values, initialized from formik values
    const [discountRateDisplay, setDiscountRateDisplay] = useState(() => decimalToPercentageString(values.discountRate));
    const [growthRateDisplay, setGrowthRateDisplay] = useState(() => decimalToPercentageString(values.growthRate));

    // Helper to convert percentage string/number from input to decimal for storage
    const percentageToDecimal = (percentageValue) => {
        if (percentageValue === null || percentageValue === undefined || String(percentageValue).trim() === '') return null;
        const num = parseFloat(percentageValue);
        if (isNaN(num)) return null;
        return num / 100;
    };


    const handleValuationDateChange = (date) => {
        setFieldValue('valuationDate', dateToString(date), true);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
                Valuation Dates & Financial Rates
            </Typography>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                    row
                    aria-label="Value entry method"
                    name="useCustomValues"
                    value={useCustomValues ?? false}
                    onChange={(event, value) => {
                        setFieldValue('useCustomValues', value === 'true');
                    }}
                >
                    <FormControlLabel value={false} control={<Radio />} label="Use Statistical Values" />
                    <FormControlLabel value={true} control={<Radio />} label="Use Custom Values" />
                </RadioGroup>
            </FormControl>

            <Typography variant="subtitle1" component="div" gutterBottom>
                Valuation Dates
            </Typography>
            <Grid container spacing={3}>
                <Grid size={{xs:6, md:4, lg: 3}}>
                    <TextField
                        required
                        id="worklifeExpectancyYears"
                        name="worklifeExpectancyYears"
                        label="Worklife Expectancy (Years remaining)"
                        type="number"
                        fullWidth
                        value={values.worklifeExpectancyYears ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{ inputProps: { step: 0.01, min: 0 } }}
                        disabled={fieldDisabled}
                        error={touched.worklifeExpectancyYears && Boolean(errors.worklifeExpectancyYears)}
                        helperText={(touched.worklifeExpectancyYears && errors.worklifeExpectancyYears) || (useCustomValues ? "Remaining years in workforce from current date." : "Remaining years in workforce from current date, based on statistical data.")}
                    />
                </Grid>
                <Grid size={{xs:6, md:4, lg: 3}}>
                    <TextField
                        id="retirementAge"
                        name="retirementAge"
                        label="Retirement Age"
                        type="number"
                        fullWidth
                        value={values.retirementAge ?? ''}
                        InputProps={{ readOnly: true, inputProps: { step: 0.01 } }}
                        disabled={true}
                        helperText="Calculated age at the expected retirement date."
                    />
                </Grid>
                <Grid size={{xs:6, md:4, lg: 3}}>
                    <TextField
                        required
                        id="lifeExpectancyYears"
                        name="lifeExpectancyYears"
                        label="Life Expectancy (Years remaining)"
                        type="number"
                        fullWidth
                        value={values.lifeExpectancyYears ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{ inputProps: { step: 0.01, min: 0 } }}
                        disabled={fieldDisabled}
                        error={touched.lifeExpectancyYears && Boolean(errors.lifeExpectancyYears)}
                        helperText={(touched.lifeExpectancyYears && errors.lifeExpectancyYears) || (useCustomValues ? "Remaining years of life from current date." : "Remaining years of life from current date, based on statistical data.")}
                    />
                </Grid>
                <Grid size={{xs:6, md:4, lg: 3}}>
                    <TextField
                        id="lifespanAge"
                        name="lifespanAge"
                        label="Projected Lifespan (Age)"
                        type="number"
                        fullWidth
                        value={values.lifespanAge ?? ''}
                        InputProps={{ readOnly: true, inputProps: { step: 0.01 } }}
                        disabled={true}
                        helperText="Calculated age at the expected end of life date."
                    />
                </Grid>
            </Grid>

            <Typography variant="subtitle1" component="div" gutterBottom>
                Financial Rates
            </Typography>
            <Grid container spacing={3}>
                <Grid size={{xs:6, md:4, lg: 3}}>
                    <DatePicker
                        label="Valuation Date"
                        value={parseDate(values.valuationDate)}
                        onChange={handleValuationDateChange}
                        onClose={() => formik.setFieldTouched('valuationDate')}
                        format="yyyy-MM-dd"
                        id="valuationDate"
                        name="valuationDate"
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                variant: 'outlined',
                                required: true,
                                error: touched.valuationDate && Boolean(errors.valuationDate),
                                helperText: (touched.valuationDate && errors.valuationDate) || (useCustomValues ? "Valuation date for present-value calculations." : "Automatically set to current date. Used for present-value calculations."),
                                onBlur: handleBlur,
                            }
                        }}
                    />
                </Grid>
                <Grid size={{xs:6, md:4, lg: 3}}>
                    <TextField
                        required
                        id="discountRate"
                        name="discountRate"
                        label="Discount Rate"
                        type="number"
                        fullWidth
                        value={useCustomValues ? discountRateDisplay : decimalToPercentageString(values.discountRate)}
                        onChange={(e) => {
                            if (useCustomValues) setDiscountRateDisplay(e.target.value);
                        }}
                        onBlur={(e) => {
                            if (useCustomValues) {
                                const decimalValue = percentageToDecimal(e.target.value);
                                setFieldValue('discountRate', decimalValue);
                                handleBlur(e);
                            }
                        }}
                        InputProps={{
                            inputProps: { step: 0.01, min: 0 },
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        disabled={fieldDisabled}
                        error={touched.discountRate && Boolean(errors.discountRate)}
                        helperText={(touched.discountRate && errors.discountRate) || "Interest rate used to calculate the present value of future earnings."}
                    />
                </Grid>
                <Grid size={{xs:6, md:4, lg: 3}}>
                    <TextField
                        required
                        id="growthRate"
                        name="growthRate"
                        label="Growth Rate"
                        type="number"
                        fullWidth
                        value={useCustomValues ? growthRateDisplay : decimalToPercentageString(values.growthRate)}
                        onChange={(e) => {
                            if (useCustomValues) setGrowthRateDisplay(e.target.value);
                        }}
                        onBlur={(e) => {
                            if (useCustomValues) {
                                const decimalValue = percentageToDecimal(e.target.value);
                                setFieldValue('growthRate', decimalValue);
                                handleBlur(e);
                            }
                        }}
                        InputProps={{
                            inputProps: { step: 0.01, min: 0 },
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        disabled={fieldDisabled}
                        error={touched.growthRate && Boolean(errors.growthRate)}
                        helperText={(touched.growthRate && errors.growthRate) || "Estimated annual income growth rate due to inflation or promotions."}
                    />
                </Grid>
                <Grid size={{xs:6, md:4, lg: 3}}>
                    <TextField
                        required
                        id="netDiscountRate"
                        name="netDiscountRate"
                        label="Net Discount Rate"
                        type="number"
                        fullWidth
                        value={decimalToPercentageString(values.netDiscountRate, 2)}
                        InputProps={{
                            readOnly: true,
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        disabled={true}
                        error={touched.netDiscountRate && Boolean(errors.netDiscountRate)}
                        helperText={(touched.netDiscountRate && errors.netDiscountRate) || "Calculated based on Discount Rate and Growth Rate."}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default DatesAndRatesStep;